import Box from "@/common/components/Box";
import CableStatus from "@/common/components/CableStatus";
import DropdownMenu from "@/common/components/DropdownMenu";
import { ForbiddenErrorMessage } from "@/common/components/ForbiddenErrorMessage";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SmallBadge from "@/common/components/SmallBadge";
import Spinner from "@/common/components/Spinner";
import { useActionCable } from "@/common/hooks/cable/useActionCable";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { cn } from "@/common/utils/cn";
import { getDateDiffFormatted } from "@/common/utils/date";
import { formatDateTime } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import { CancelMaintenanceWorkflowForwadingModal } from "@/features/maintenanceWorkflow/pages/Show/components/CancelMaintenanceWorkflowFowardingModal";
import AuctionProposal from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/AuctionProposal";
import AuctionTimer from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/AuctionTimer";
import MaintenanceEstablishmentChoiceAuctionEntriesShowLoading from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/MaintenanceEstablishmentChoiceAuctionEntriesShowLoading";
import MaintenanceEstablishmentChoiceEntryAuctionHeader from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/MaintenanceEstablishmentChoiceEntryAuctionHeader";
import AcceptProposal from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/modals/AcceptProposal";
import DisqualifyProposal from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/modals/DisqualifyProposal";
import UpdateAuctionDates from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/modals/UpdateAuctionDates";
import AuctionEntryEmpty from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/status/AuctionEntryEmpty";
import AuctionEntryPending from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/status/AuctionEntryPending";
import { AUCTION_BIDDING_CHANNEL } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/constants/auctionChannel.constant";
import { auctionEntryKind } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/constants/auctionEntryKind.constant";
import { auctionEntryStatus } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/constants/auctionEntryStatus.constant";
import { AUCTION_VIEW_PERMISSIONS } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/constants/auctionPermissions.constant";
import { useMaintenanceEstablishmentChoiceAuctionEntryPause } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/hooks/useMaintenanceEstablishmentChoiceAuctionEntryPause";
import { useMaintenanceEstablishmentChoiceAuctionEntryUnpause } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/hooks/useMaintenanceEstablishmentChoiceAuctionEntryUnpause";
import { useMaintenanceEstablishmentChoiceAuctionEntryUpdatedSummary } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/hooks/useMaintenanceEstablishmentChoiceAuctionEntryUpdatedSummary";
import { MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/types/updatedSummary.type";
import { useMaintenanceForwarding } from "@/features/maintenanceWorkflow/pages/Show/features/forwardings/hooks/useMaintenanceForwarding";
import { useQueryClient } from "@tanstack/react-query";
import { useServerNow } from "@shared/hooks/useServerTime";
import { CalendarCheckIcon, CalendarClockIcon, CalendarXIcon, CheckCheckIcon, ClockIcon, EllipsisIcon, FilesIcon, GlobeIcon, PauseIcon, PlayIcon, ScanEyeIcon, UserCheckIcon, XIcon } from "lucide-react";
import { getUserTimezoneFormatted } from "@/common/utils/dateUtils";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { MaintenanceForwardingEstablishmentChoiceEntryAuctionEntryData } from "../../../../../forwardings/types/establishmentChoiceEntries/auctionEntry/auctionEntry.type";
import { MaintenanceForwardingData, MaintenanceForwardingDirectiveEntryExecutionEntryData } from "../../../../../forwardings/types/maintenanceForwarding.type";

const CompanyMaintenanceWorkflowsEstablishmentChoiceEntriesAuction = () => {
  const { id: workflowId, forwardingId } = useParams();

  const queryClient = useQueryClient();

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);

  const [updateDatesIsOpen, setUpdateDatesIsOpen] = useState(false);

  const [cancelForwardingIsOpen, setCancelForwardingIsOpen] = useState(false);

  const [proposalToDisqualify, setProposalToDisqualify] = useState<
    MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData | null
  >(null);

  const [proposalToAccept, setProposalToAccept] = useState<
    MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData | null
  >(null);

  const {
    data: forwarding = {} as MaintenanceForwardingData<
      MaintenanceForwardingDirectiveEntryExecutionEntryData,
      MaintenanceForwardingEstablishmentChoiceEntryAuctionEntryData
    >,
    isPending: forwardingIsPending,
    dataUpdatedAt: forwardingUpdatedAt,
  } = useMaintenanceForwarding(forwardingId);

  const establishmentChoiceEntryId = forwarding?.establishment_choice_entry?.id;

  const {
    data: auctionSummary,
    error: auctionSummaryError,
    isLoading: auctionSummaryIsLoading,
  } = useMaintenanceEstablishmentChoiceAuctionEntryUpdatedSummary({
    establishmentChoiceEntryId,
  });

  const isAuctionForbidden = auctionSummaryError?.response?.status === 403;
  const isAuctionNotInProgress = auctionSummaryError?.response?.status === 422;

  const hasAuctionViewPermissions =
    isUserPermissionsPending || AUCTION_VIEW_PERMISSIONS.every((permission) => hasUserPermission(permission));

  const cannotViewAuction = isAuctionForbidden || !hasAuctionViewPermissions;

  const { status } = useActionCable({
    channel: AUCTION_BIDDING_CHANNEL,
    params: {
      auction_entry_id: establishmentChoiceEntryId,
    },
    enabled: !!establishmentChoiceEntryId && hasAuctionViewPermissions,
    onMessage: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] });

      queryClient.invalidateQueries({
        queryKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdatedSummary', establishmentChoiceEntryId],
      });
    },
  });

  const {
    mutate: pause,
    isPending: pauseIsPending,
  } = useMaintenanceEstablishmentChoiceAuctionEntryPause({
    establishmentChoiceId: establishmentChoiceEntryId,
    options: {
      onSuccess: () => {
        setDropdownIsOpen(false);

        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] });

        queryClient.invalidateQueries({
          queryKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdatedSummary', establishmentChoiceEntryId],
        });
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({ setMessages })(error);
      },
    },
  });

  const {
    mutate: unpause,
    isPending: unpauseIsPending,
  } = useMaintenanceEstablishmentChoiceAuctionEntryUnpause({
    establishmentChoiceId: establishmentChoiceEntryId,
    options: {
      onSuccess: () => {
        setDropdownIsOpen(false);

        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] });

        queryClient.invalidateQueries({
          queryKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdatedSummary', establishmentChoiceEntryId],
        });
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({ setMessages })(error);
      },
    },
  });

  const serverNow = useServerNow();

  const isAuctionFinished = auctionSummary?.status === 'completed' || (auctionSummary?.finish_at
    ? (new Date(auctionSummary?.finish_at)).getTime() < serverNow
    : true);

  const isAuctionCanceled =
    (auctionSummary?.status ?? forwarding?.establishment_choice_entry?.status) === 'canceled';

  const isAuctionPaused = auctionSummary?.status === 'in_progress' && !!auctionSummary?.paused_at && !isAuctionFinished;

  const canAuctionBeConcluded = Boolean(auctionSummary?.status === 'in_progress' && (auctionSummary?.paused_at || isAuctionFinished));


  return (
    <>
      <div className="inner-screen">
        <MaintenanceEstablishmentChoiceEntryAuctionHeader
          actions={cannotViewAuction ? undefined : (
            <div className="flex gap-3">
              <CableStatus status={status} />

              <DropdownMenu
                isOpen={dropdownIsOpen}
                setIsOpen={setDropdownIsOpen}
                triggerLabel="Ações"
                triggerClassName="bg-primary-blue hover:bg-primary-blue-hover transition-colors focus:ring-0 focus:outline-none focus-visible:ring-0"
                TriggerIcon={<EllipsisIcon size={20} />}
                contentClassName="bg-black-900 p-0"
                menuPositionAlign="end"
                itemClassName="transition-colors w-48 [&>*]:justify-between"
                options={[
                  {
                    label: (
                      <>
                        Aprovar Leilão

                        <CheckCheckIcon
                          strokeWidth={1.5}
                          size={20}
                          className="text-success-tertiary"
                        />
                      </>
                    ),
                    onClick: (event) => {
                      event.stopPropagation();

                      if (auctionSummary?.best_proposal)
                        setProposalToAccept(auctionSummary.best_proposal);

                      setDropdownIsOpen(false);
                    },
                    className: cn((!canAuctionBeConcluded || !auctionSummary?.best_proposal?.id) && 'hidden'),
                    operationKlassToCheckPermission: 'Client::Maintenance::EstablishmentChoice::AuctionEntryProposals::Accept',
                  },
                  {
                    label: (
                      <>
                        Alterar Datas

                        <CalendarClockIcon
                          strokeWidth={1.5}
                          size={20}
                          className="text-blue-800"
                        />
                      </>
                    ),
                    onClick: (event) => {
                      event.stopPropagation();

                      setUpdateDatesIsOpen(true);

                      setDropdownIsOpen(false);
                    },
                    className: cn(auctionSummary?.status !== 'in_progress' && 'hidden'),
                    operationKlassToCheckPermission: 'Client::Maintenance::EstablishmentChoice::AuctionEntries::UpdateDates',
                  },
                  {
                    label: (
                      <>
                        Pausar

                        {pauseIsPending ? (
                          <Spinner className="size-5" />
                        ) : (
                          <PauseIcon
                            strokeWidth={1.5}
                            size={20}
                            className="text-warning"
                          />
                        )}
                      </>
                    ),
                    onClick: (event) => {
                      event.stopPropagation();

                      pause();
                    },
                    disabled: pauseIsPending,
                    className: cn((auctionSummary?.paused_at || canAuctionBeConcluded) && 'hidden'),
                    operationKlassToCheckPermission: 'Client::Maintenance::EstablishmentChoice::AuctionEntries::Pause',
                  },
                  {
                    label: (
                      <>
                        Retomar

                        {unpauseIsPending ? (
                          <Spinner className="size-5" />
                        ) : (
                          <PlayIcon strokeWidth={1.5} size={20} />
                        )}
                      </>
                    ),
                    onClick: (event) => {
                      event.stopPropagation();

                      unpause();
                    },
                    disabled: unpauseIsPending,
                    className: cn('bg-success-op/30 hover:bg-success-op/40', !isAuctionPaused && 'hidden'),
                    operationKlassToCheckPermission: 'Client::Maintenance::EstablishmentChoice::AuctionEntries::Unpause',
                  },
                  {
                    label: (
                      <>
                        Cancelar Leilão

                        <XIcon
                          strokeWidth={1.5}
                          size={20}
                          className="text-danger"
                        />
                      </>
                    ),
                    onClick: (event) => {
                      event.stopPropagation();

                      setCancelForwardingIsOpen(true);

                      setDropdownIsOpen(false);
                    },
                    className: cn(!['in_progress', 'pending'].includes(auctionSummary?.status || '') && 'hidden'),
                    operationKlassToCheckPermission: 'Client::Maintenance::Forwardings::Cancel',
                  },
                ]}
              />
            </div>
          )}
        />

        {(forwardingIsPending || auctionSummaryIsLoading) && !cannotViewAuction && (
          <MaintenanceEstablishmentChoiceAuctionEntriesShowLoading />
        )}

        {!forwardingIsPending && cannotViewAuction && (
          <ForbiddenErrorMessage className="h-full flex-1" />
        )}

        {!forwardingIsPending && !auctionSummaryIsLoading && !cannotViewAuction && (
          <div className="flex-1 overflow-hidden flex flex-col gap-3">
            <div className="flex items-center gap-4">
              <Box className={cn('flex-1 p-3 rounded-lg flex flex-col gap-2', isAuctionCanceled && 'bg-danger-op', isAuctionPaused && 'bg-yellow-op-2')}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <p className="text-base font-bold">
                      Leilão
                    </p>

                    <p className={cn('text-xs text-valid', isAuctionCanceled && 'text-danger')}>
                      {(auctionSummary?.status ?? forwarding?.establishment_choice_entry?.status) &&
                        auctionEntryStatus[auctionSummary?.status ?? forwarding?.establishment_choice_entry?.status ?? '']}
                    </p>
                  </div>

                  <div className="flex items-center gap-1 flex-wrap">
                    <SmallBadge
                      title="Tipo de leilão"
                      LeftIcon={ScanEyeIcon}
                      text={auctionSummary?.kind ? auctionEntryKind[auctionSummary?.kind] : '--'}
                    />

                    <SmallBadge
                      title="Duração do leilão"
                      LeftIcon={ClockIcon}
                      text={
                        auctionSummary?.finish_at
                          ? `${getDateDiffFormatted(
                            auctionSummary?.finish_at ? new Date(auctionSummary?.finish_at) : new Date(),
                            auctionSummary?.start_at ? new Date(auctionSummary?.start_at) : new Date()
                          )}`
                          : '--'
                      }
                    />

                    <SmallBadge
                      title="Data de início"
                      LeftIcon={CalendarCheckIcon}
                      text={auctionSummary?.start_at ? formatDateTime(new Date(auctionSummary?.start_at)) : 'Imediato'}
                    />

                    <SmallBadge
                      title="Data de encerramento"
                      LeftIcon={CalendarXIcon}
                      text={auctionSummary?.finish_at ? formatDateTime(new Date(auctionSummary?.finish_at)) : '--'}
                    />

                    <SmallBadge
                      title="Fuso horário"
                      LeftIcon={GlobeIcon}
                      text={getUserTimezoneFormatted()}
                    />
                  </div>
                </div>

                <div className="flex items-end justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1 flex-wrap">
                    <SmallBadge
                      LeftIcon={UserCheckIcon}
                      text={
                        auctionSummary?.participants_count != null
                          ? `${auctionSummary?.participants_count} Participando`
                          : '--'
                      }
                    />

                    <SmallBadge
                      LeftIcon={FilesIcon}
                      text={
                        auctionSummary?.proposals_count != null
                          ? `${auctionSummary?.proposals_count} Enviadas`
                          : '--'
                      }
                    />
                  </div>
                </div>
              </Box>

              {auctionSummary?.finish_at && (
                <AuctionTimer
                  startDate={auctionSummary?.start_at ? new Date(auctionSummary?.start_at) : new Date()}
                  endDate={auctionSummary?.finish_at ? new Date(auctionSummary?.finish_at) : new Date()}
                  isPaused={!!auctionSummary?.paused_at}
                  isFinished={!!isAuctionFinished}
                  isCanceled={isAuctionCanceled}
                />
              )}
            </div>

            {forwarding?.establishment_choice_entry?.status !== 'pending' && (
              <Box className="flex-1 overflow-auto thin-scroll p-3 rounded-lg flex flex-col gap-2">
                {isAuctionNotInProgress ? (
                  <div className="flex-1 flex flex-col items-center justify-center gap-4">
                    <p className="text-base font-bold text-center">
                      {forwarding?.establishment_choice_entry?.status === 'completed'
                        ? 'Este leilão foi finalizado'
                        : 'Este leilão foi cancelado'}
                    </p>
                  </div>
                ) : auctionSummary?.proposals && Object.keys(auctionSummary?.proposals).length > 0 ? (
                  Object.entries(auctionSummary?.proposals)?.map(([proposalId, proposal]) => (
                    <AuctionProposal
                      key={proposalId}
                      proposal={proposal}
                      directiveEntry={forwarding?.directive_entry}
                      directiveEntryType={auctionSummary?.directive_entry_type}
                      canAccept={canAuctionBeConcluded && proposal.position === 1}
                      canDisqualify={auctionSummary?.status === 'in_progress'}
                      onAccept={() => setProposalToAccept(proposal)}
                      onDisqualify={() => setProposalToDisqualify(proposal)}
                      forwardingUpdatedAt={forwardingUpdatedAt}
                    />
                  ))
                ) : (
                  <AuctionEntryEmpty />
                )}
              </Box>
            )}

            {forwarding?.establishment_choice_entry?.status === 'pending' && (
              <Box className="flex-1 overflow-hidden p-4 flex flex-col gap-4">
                <AuctionEntryPending />
              </Box>
            )}
          </div>
        )}
      </div>

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />

      <AcceptProposal
        proposal={proposalToAccept}
        onDismiss={() => setProposalToAccept(null)}
      />

      <DisqualifyProposal
        proposal={proposalToDisqualify}
        onDismiss={() => setProposalToDisqualify(null)}
      />

      <UpdateAuctionDates
        isOpen={updateDatesIsOpen}
        setIsOpen={setUpdateDatesIsOpen}
        establishmentChoiceEntryId={establishmentChoiceEntryId}
        forwardingId={forwardingId}
        startAt={auctionSummary?.start_at}
        finishAt={auctionSummary?.finish_at}
      />

      <CancelMaintenanceWorkflowForwadingModal
        isOpen={cancelForwardingIsOpen}
        setIsOpen={setCancelForwardingIsOpen}
        workflowForwadingId={forwardingId}
        workflowId={workflowId}
      />
    </>
  );
}

export default CompanyMaintenanceWorkflowsEstablishmentChoiceEntriesAuction;
